/**
 * Accès à l'API depuis le back-office.
 *
 * Les cookies de session voyagent avec chaque requête (`credentials`), d'où la
 * liste d'origines autorisées côté serveur.
 */

import type { CatalogResponse, FestivalSummary } from "@festipal/shared";

// Réexporté pour que les écrans n'aient qu'un seul endroit où regarder.
export type { FestivalSummary };

export const API_BASE_URL =
  import.meta.env["VITE_API_URL"] ?? "https://festipal-api.loklakh.fr";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
  }
}

export async function apiFetch<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? "GET",
      headers: options.body ? { "content-type": "application/json" } : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: "include",
    });
  } catch {
    // Le navigateur n'a pas pu joindre l'API : réseau coupé, requête bloquée par
    // une extension, ou serveur injoignable. C'est un cas distinct d'une réponse
    // d'erreur, et le confondre avec elle envoie chercher un mot de passe faux
    // là où le problème est ailleurs.
    throw new ApiError(0, "unreachable");
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(response.status, payload.error ?? "unknown_error");
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export type SessionUser = {
  id: string;
  displayName: string;
  locale: string;
  accountType: string;
  role: string;
};

export const session = {
  me: () => apiFetch<SessionUser>("/auth/me"),
  login: (email: string, password: string) =>
    apiFetch<SessionUser>("/auth/login", { method: "POST", body: { email, password } }),
  logout: () => apiFetch<void>("/auth/logout", { method: "POST" }),
  /** "Forgot my password": a six-digit code by e-mail, then the new password. */
  requestReset: (email: string) =>
    apiFetch<void>("/auth/password-reset", { method: "POST", body: { email } }),
  confirmReset: (email: string, code: string, password: string) =>
    apiFetch<void>("/auth/password-reset/confirm", {
      method: "POST",
      body: { email, code, password },
    }),
};

export type PublicationState = {
  publishedAt: string | null;
  publishedHash: string | null;
  draftHash: string | null;
  hasUnpublishedChanges: boolean;
};

export const invitations = {
  list: () =>
    apiFetch<
      {
        code: string;
        label: string | null;
        expiresAt: string;
        state: "open" | "used" | "expired";
        usedBy: string | null;
      }[]
    >("/invitations"),

  create: (label: string, days: number) =>
    apiFetch<{ code: string; label: string | null; expiresAt: string }>(
      "/invitations",
      {
        method: "POST",
        body: { ...(label ? { label } : {}), days },
      },
    ),
};

export type ReportStatus = "OPEN" | "RESOLVED" | "DISMISSED";

export type ErrorReport = {
  id: string;
  targetType: "PERFORMANCE" | "POI" | "FESTIVAL";
  kind: string;
  comment: string | null;
  status: ReportStatus;
  createdAt: string;
  handledAt: string | null;
  /** What the report is about; null for the whole festival, or once deleted. */
  target:
    | { name: string; stage: string | null; startsAt: string }
    | { name: string | null; category: string }
    | null;
};

/** Where a community page's lineup comes from. */
export type LineupSource = {
  community: boolean;
  label: string | null;
  url: string | null;
  /** YYYY-MM-DD. */
  checkedOn: string | null;
};

export const festivals = {
  /**
   * Le catalogue rend `{ total, festivals }` ; le déballage est fait ici pour
   * que les appelants reçoivent une liste.
   *
   * Le type vient du paquet partagé, comme celui du serveur : c'est ce qui
   * empêche de refaire l'erreur qui a rendu cette page noire — une forme
   * changée d'un côté, oubliée de l'autre.
   */
  list: async () => (await apiFetch<CatalogResponse>("/festivals")).festivals,
  bundle: (slug: string) => apiFetch<unknown>(`/festivals/${slug}/bundle`),
  publication: (slug: string) =>
    apiFetch<PublicationState>(`/admin/festivals/${slug}/publication`),
  publish: (slug: string) =>
    apiFetch<{ contentHash: string; pois: number; performances: number }>(
      `/admin/festivals/${slug}/publish`,
      { method: "POST" },
    ),
  claim: (slug: string) =>
    apiFetch<{ slug: string }>(`/admin/festivals/${slug}/claim`, { method: "POST" }),
  update: (slug: string, body: unknown) =>
    apiFetch<{ slug: string }>(`/admin/festivals/${slug}`, { method: "PATCH", body }),
  createPoi: (slug: string, body: unknown) =>
    apiFetch<{ id: string }>(`/admin/festivals/${slug}/pois`, { method: "POST", body }),
  deletePoi: (slug: string, poiId: string) =>
    apiFetch<void>(`/admin/festivals/${slug}/pois/${poiId}`, { method: "DELETE" }),

  createStage: (slug: string, body: unknown) =>
    apiFetch<{ id: string }>(`/admin/festivals/${slug}/stages`, {
      method: "POST",
      body,
    }),
  deleteStage: (slug: string, stageId: string) =>
    apiFetch<void>(`/admin/festivals/${slug}/stages/${stageId}`, { method: "DELETE" }),

  createPerformance: (slug: string, body: unknown) =>
    apiFetch<{ id: string }>(`/admin/festivals/${slug}/performances`, {
      method: "POST",
      body,
    }),
  updatePerformance: (slug: string, id: string, body: unknown) =>
    apiFetch<void>(`/admin/festivals/${slug}/performances/${id}`, {
      method: "PATCH",
      body,
    }),
  plan: (slug: string) =>
    apiFetch<{
      width: number;
      height: number;
      controlPoints: { x: number; y: number; latitude: number; longitude: number }[];
      rmseM: number | null;
      residuals: number[];
      accuracy: string | null;
    }>(`/admin/festivals/${slug}/plan`),

  uploadPlan: async (slug: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(`${API_BASE_URL}/admin/festivals/${slug}/plan`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(response.status, payload.error ?? "upload_failed");
    }
    return (await response.json()) as { width: number; height: number };
  },

  saveControlPoints: (slug: string, points: unknown[]) =>
    apiFetch<{
      points: number;
      rmseM: number | null;
      residuals: number[];
      accuracy: string | null;
    }>(`/admin/festivals/${slug}/plan/control-points`, {
      method: "PUT",
      body: { points },
    }),

  fetchSource: (slug: string, url: string) =>
    apiFetch<{ text: string; finalUrl: string }>(
      `/admin/festivals/${slug}/import/fetch`,
      { method: "POST", body: { url } },
    ),

  importPdf: async (slug: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(`${API_BASE_URL}/admin/festivals/${slug}/import/pdf`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        reason?: string;
      };
      throw new ApiError(
        response.status,
        payload.reason ?? payload.error ?? "pdf_failed",
      );
    }
    return (await response.json()) as { text: string; pages: number };
  },

  theme: (slug: string) =>
    apiFetch<{ inks: Record<string, string> | null }>(`/admin/festivals/${slug}/theme`),

  saveTheme: (slug: string, inks: Record<string, string> | null) =>
    apiFetch<{ themed: boolean }>(`/admin/festivals/${slug}/theme`, {
      method: "PUT",
      body: { inks },
    }),

  contributions: (slug: string, status: "PENDING" | "ACCEPTED" | "REJECTED") =>
    apiFetch<{
      contributions: {
        id: string;
        category: string;
        name: string;
        locale: string;
        comment: string | null;
        latitude: number;
        longitude: number;
        accuracyM: number;
        sampleCount: number;
        capturedAt: string;
        status: string;
        hasPhoto: boolean;
        author: { id: string; displayName: string };
      }[];
    }>(`/admin/festivals/${slug}/contributions?status=${status}`),

  reviewContribution: (slug: string, id: string, decision: "accept" | "reject") =>
    apiFetch<{ id: string; status: string; poiId?: string }>(
      `/admin/festivals/${slug}/contributions/${id}/${decision}`,
      { method: "POST" },
    ),

  reports: (slug: string, status: ReportStatus) =>
    apiFetch<{ open: number; reports: ErrorReport[] }>(
      `/admin/festivals/${slug}/reports?status=${status}`,
    ),

  reviewReport: (slug: string, id: string, status: ReportStatus) =>
    apiFetch<void>(`/admin/festivals/${slug}/reports/${id}`, {
      method: "POST",
      body: { status },
    }),

  source: (slug: string) => apiFetch<LineupSource>(`/admin/festivals/${slug}/source`),

  saveSource: (slug: string, body: Omit<LineupSource, "community">) =>
    apiFetch<void>(`/admin/festivals/${slug}/source`, { method: "PUT", body }),

  contributionPhotoUrl: (slug: string, id: string) =>
    `${API_BASE_URL}/admin/festivals/${slug}/contributions/${id}/photo`,

  orthophoto: (slug: string) =>
    apiFetch<{ present: boolean; bytes?: number; minZoom?: number; maxZoom?: number }>(
      `/admin/festivals/${slug}/orthophoto`,
    ),

  /**
   * Dépôt de l'orthophoto, avec avancement.
   *
   * `XMLHttpRequest` plutôt que `fetch` : c'est le seul moyen de connaître
   * l'avancement d'un envoi, et une archive de plusieurs centaines de
   * mégaoctets sans barre de progression donne l'impression d'un plantage.
   */
  uploadOrthophoto: (slug: string, file: File, onProgress: (ratio: number) => void) =>
    new Promise<{ bytes: number; minZoom: number; maxZoom: number }>(
      (resolve, reject) => {
        const form = new FormData();
        form.append("file", file);

        const request = new XMLHttpRequest();
        request.open("PUT", `${API_BASE_URL}/admin/festivals/${slug}/orthophoto`);
        request.withCredentials = true;

        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) onProgress(event.loaded / event.total);
        });

        request.addEventListener("load", () => {
          if (request.status >= 200 && request.status < 300) {
            resolve(JSON.parse(request.responseText));
          } else {
            const payload = (() => {
              try {
                return JSON.parse(request.responseText) as { error?: string };
              } catch {
                return {};
              }
            })();
            reject(new ApiError(request.status, payload.error ?? "upload_failed"));
          }
        });
        request.addEventListener("error", () => reject(new ApiError(0, "network")));
        request.send(form);
      },
    ),

  deleteOrthophoto: (slug: string) =>
    apiFetch<void>(`/admin/festivals/${slug}/orthophoto`, { method: "DELETE" }),

  planImageUrl: (slug: string) => `${API_BASE_URL}/admin/festivals/${slug}/plan/image`,

  importLineup: (slug: string, body: unknown) =>
    apiFetch<{
      headers?: string[];
      columns?: Record<string, string>;
      issues: { line: number; message: string }[];
      performances?: {
        stage: string;
        artist: string;
        startsAt: string;
        endsAt: string;
      }[];
      total?: number;
      created?: number;
    }>(`/admin/festivals/${slug}/lineup/import`, { method: "POST", body }),

  deletePerformance: (slug: string, id: string) =>
    apiFetch<void>(`/admin/festivals/${slug}/performances/${id}`, {
      method: "DELETE",
    }),
};

export type StaffShift = {
  id?: string;
  email: string;
  name?: string | null;
  team?: string | null;
  post: string;
  place?: string | null;
  startsAt: string;
  endsAt: string;
  referent?: string | null;
  instructions?: string | null;
};

export type StaffState = {
  timezone: string;
  staffUntilDefault: string;
  members: {
    userId: string;
    displayName: string;
    email: string | null;
    isOrganizer: boolean;
    isStaff: boolean;
    staffUntil: string | null;
  }[];
  people: {
    email: string;
    name: string | null;
    shifts: number;
    hasAccount: boolean;
    isStaff: boolean;
  }[];
  shifts: StaffShift[];
};

/** Team and plannings of a festival (STAFF.md §5): organisers only. */
export const staff = {
  get: (slug: string) => apiFetch<StaffState>(`/admin/festivals/${slug}/staff`),
  importPlanning: (
    slug: string,
    body: { content: string; defaultDay?: string; dryRun?: boolean },
  ) =>
    apiFetch<{
      headers?: string[];
      columns?: Record<string, string>;
      issues: { line: number; message: string }[];
      shifts?: StaffShift[];
      total?: number;
      created?: number;
      people: number;
      withAccount: number;
    }>(`/admin/festivals/${slug}/staff/import`, { method: "POST", body }),
  setRole: (
    slug: string,
    body: { email: string; staff?: boolean; organizer?: boolean },
  ) =>
    apiFetch<void>(`/admin/festivals/${slug}/staff/members`, { method: "PUT", body }),
  grantPlanned: (slug: string) =>
    apiFetch<{ granted: number }>(`/admin/festivals/${slug}/staff/grant-planned`, {
      method: "POST",
    }),
};

/** Home-screen icon of an official festival (DESIGN.md §4.2). */
export const launcherIcon = {
  state: (slug: string) =>
    apiFetch<{ official: boolean; present: boolean; version: string | null }>(
      `/admin/festivals/${slug}/launcher-icon`,
    ),
  imageUrl: (slug: string, version: string) =>
    `${API_BASE_URL}/admin/festivals/${slug}/launcher-icon.png?v=${version}`,
  upload: async (slug: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(
      `${API_BASE_URL}/admin/festivals/${slug}/launcher-icon`,
      {
        method: "POST",
        body: form,
        credentials: "include",
      },
    );
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(response.status, payload.error ?? "upload_failed");
    }
    return (await response.json()) as { version: string };
  },
  remove: (slug: string) =>
    apiFetch<void>(`/admin/festivals/${slug}/launcher-icon`, { method: "DELETE" }),
};
